import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Clock3,
  Info,
  RefreshCw,
  Search,
  TriangleAlert,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  type RealtimeProduct,
  type RealtimeProductMetrics,
  type RealtimeSummary,
  useRealtimeData,
} from '../realtime'
import type { ProductLevel } from '../types'
import { integer, money, percent } from '../utils'

type SortKey = 'gmv' | 'delta' | 'soldItemCount' | 'productVisitors' | 'conversionRate'

const timeLabel = (value: string, seconds = false) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    second: seconds ? '2-digit' : undefined,
    hour12: false,
  }).format(date)
}

const dateTimeLabel = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date).replaceAll('/', '-')
}

const metricDelta = (current: number, previous?: number | null) =>
  previous === null || previous === undefined ? null : current - previous

const metricDeltaRate = (current: number, previous?: number | null) => {
  if (previous === null || previous === undefined) return null
  if (previous === 0) return current === 0 ? 0 : null
  return (current / previous - 1) * 100
}

function DeltaText({
  current,
  previous,
  formatter,
  previousTime,
}: {
  current: number
  previous?: number | null
  formatter: (value: number) => string
  previousTime?: string
}) {
  const delta = metricDelta(current, previous)
  const rate = metricDeltaRate(current, previous)
  if (delta === null) return <span className="realtime-metric-context">首份快照，暂无时段变化</span>
  const direction = delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral'
  return (
    <span className={`realtime-metric-context is-${direction}`}>
      {previousTime ? `较 ${previousTime}` : '较上次'} {delta > 0 ? '+' : ''}{formatter(delta)}
      {rate === null ? '' : ` · ${rate > 0 ? '+' : ''}${rate.toFixed(2)}%`}
    </span>
  )
}

const effectiveMetrics = (
  product: RealtimeProduct,
  usePrevious: boolean,
): RealtimeProductMetrics => {
  if (!usePrevious || !product.previousMetrics) return product.metrics
  return {
    ...product.metrics,
    ...product.previousMetrics,
  }
}

export function RealtimePage() {
  const { data, loading, error, reload } = useRealtimeData()
  const [level, setLevel] = useState<ProductLevel>('spu')
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('gmv')
  const [selectedRunId, setSelectedRunId] = useState('')

  const selectableSnapshots = useMemo(
    () => data?.snapshots.slice(-2) || [],
    [data],
  )
  const effectiveRunId = selectedRunId || data?.meta.latestRunId || ''
  const selectedSnapshot = selectableSnapshots.find((snapshot) => snapshot.runId === effectiveRunId)
    || selectableSnapshots[selectableSnapshots.length - 1]
  const latestSnapshot = data?.snapshots[data.snapshots.length - 1]
  const previousSnapshot = data && data.snapshots.length > 1
    ? data.snapshots[data.snapshots.length - 2]
    : null
  const isLatest = selectedSnapshot?.runId === data?.meta.latestRunId
  const usePreviousProductMetrics = Boolean(
    previousSnapshot && selectedSnapshot?.runId === previousSnapshot.runId,
  )
  const summary = selectedSnapshot?.spu || data?.summary
  const comparison = isLatest ? data?.previousSummary : null

  const products = useMemo(() => {
    if (!data) return []
    const query = search.trim().toLowerCase()
    return data.products
      .filter((product) => product.level === level)
      .filter((product) => !query || [
        product.name,
        product.sourceName,
        product.id,
        product.merchantSku || '',
      ].join(' ').toLowerCase().includes(query))
      .sort((a, b) => {
        const aMetrics = effectiveMetrics(a, usePreviousProductMetrics)
        const bMetrics = effectiveMetrics(b, usePreviousProductMetrics)
        if (sortKey === 'delta') {
          const aDelta = a.previousMetrics ? a.metrics.gmv - a.previousMetrics.gmv : 0
          const bDelta = b.previousMetrics ? b.metrics.gmv - b.previousMetrics.gmv : 0
          return bDelta - aDelta
        }
        return Number(bMetrics[sortKey] || 0) - Number(aMetrics[sortKey] || 0)
      })
      .slice(0, 8)
  }, [data, level, search, sortKey, usePreviousProductMetrics])

  const movers = useMemo(() => {
    if (!data || !previousSnapshot) return { gains: [], declines: [] }
    const rows = data.products
      .filter((product) => product.level === 'spu' && product.previousMetrics)
      .map((product) => ({
        product,
        delta: product.metrics.gmv - (product.previousMetrics?.gmv || 0),
      }))
    return {
      gains: rows.filter((item) => item.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 5),
      declines: rows.filter((item) => item.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, 5),
    }
  }, [data, previousSnapshot])

  if (loading && !data) return <div className="loading-state">正在加载经营快照…</div>
  if (error && !data) return <div className="error-state"><h1>实时数据加载失败</h1><p>{error}</p></div>
  if (!data || !summary || !latestSnapshot) return null

  const previousTime = previousSnapshot ? timeLabel(previousSnapshot.snapshotAt) : undefined
  const chartRows = data.snapshots.map((snapshot) => ({
    time: timeLabel(snapshot.snapshotAt),
    gmv: snapshot.spu.gmv,
    visitors: snapshot.spu.productVisitors,
  }))

  const summaryCards: Array<{
    label: string
    value: string
    current: number
    previous?: number | null
    formatter: (value: number) => string
  }> = [
    {
      label: '实时成交金额',
      value: money(summary.gmv),
      current: summary.gmv,
      previous: comparison?.gmv,
      formatter: money,
    },
    {
      label: '成交件数',
      value: integer(summary.soldItemCount),
      current: summary.soldItemCount,
      previous: comparison?.soldItemCount,
      formatter: integer,
    },
    {
      label: '商品访客',
      value: integer(summary.productVisitors),
      current: summary.productVisitors,
      previous: comparison?.productVisitors,
      formatter: integer,
    },
    {
      label: '商品浏览量',
      value: integer(summary.productPageviews),
      current: summary.productPageviews,
      previous: comparison?.productPageviews,
      formatter: integer,
    },
    {
      label: '成交转化率',
      value: percent(summary.conversionRate),
      current: summary.conversionRate,
      previous: comparison?.conversionRate,
      formatter: (value) => `${value.toFixed(2)}pt`,
    },
    {
      label: '动销SPU',
      value: integer(summary.activeProducts),
      current: summary.activeProducts,
      previous: comparison?.activeProducts,
      formatter: integer,
    },
  ]

  return (
    <div className="page-stack realtime-page">
      <div className="page-title-row realtime-title-row">
        <div>
          <h1>经营快照（暂停更新）</h1>
          <p>
            最后快照 {data.meta.businessDate} {timeLabel(latestSnapshot.snapshotAt)} · 仅供历史查看
          </p>
        </div>
        <div className="realtime-title-actions">
          <DataFreshnessBadge date={data.meta.businessDate} label="快照数据截至" mode="paused" />
          <button type="button" className="realtime-refresh-button" onClick={() => void reload()}>
            <RefreshCw aria-hidden="true" />刷新本地快照
          </button>
          <label className="realtime-snapshot-select">
            <span className="sr-only">选择实时快照</span>
            <Clock3 aria-hidden="true" />
            <select
              value={selectedSnapshot?.runId || ''}
              onChange={(event) => setSelectedRunId(event.target.value)}
            >
              {selectableSnapshots.map((snapshot) => (
                <option key={snapshot.runId} value={snapshot.runId}>
                  {timeLabel(snapshot.snapshotAt)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="realtime-scope-notice is-paused">
        <Info aria-hidden="true" />
        <span>该板块已暂停更新；当前是历史实时快照，不代表现在的经营情况。需要新数据时，在频道说一声由AI按需采集。</span>
      </div>

      <section className="realtime-kpi-grid" aria-label="历史经营快照核心指标">
        {summaryCards.map((item) => (
          <article key={item.label} className="realtime-kpi">
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <DeltaText
              current={item.current}
              previous={item.previous}
              formatter={item.formatter}
              previousTime={previousTime}
            />
          </article>
        ))}
      </section>

      <section className="realtime-main-grid">
        <div className="realtime-panel realtime-trend-panel">
          <div className="realtime-panel-heading">
            <div>
              <h2>快照当日累计成交走势</h2>
              <p>{data.meta.snapshotCount} 份只读快照 · 只比较盘中采集点</p>
            </div>
            <div className="realtime-chart-legend" aria-hidden="true">
              <span><i className="is-gmv" />成交金额</span>
              <span><i className="is-visitors" />商品访客</span>
            </div>
          </div>
          {chartRows.length > 1 ? (
            <div className="realtime-chart" role="img" aria-label="今日实时成交金额和商品访客累计走势">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartRows} margin={{ top: 16, right: 16, bottom: 2, left: 2 }}>
                  <CartesianGrid stroke="#e6ecf4" vertical={false} />
                  <XAxis dataKey="time" tick={{ fill: '#667085', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#dde4ee' }} />
                  <YAxis yAxisId="gmv" width={58} tickFormatter={(value) => `${Math.round(Number(value) / 10000)}万`} tick={{ fill: '#667085', fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="visitors" orientation="right" width={54} tickFormatter={(value) => `${Math.round(Number(value) / 10000)}万`} tick={{ fill: '#667085', fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip
                    formatter={(value, name) => [
                      name === '成交金额' ? money(Number(value)) : integer(Number(value)),
                      name,
                    ]}
                    contentStyle={{ border: '1px solid #dde4ee', borderRadius: 4, boxShadow: '0 8px 24px rgba(16,24,40,.12)' }}
                  />
                  <Line yAxisId="gmv" dataKey="gmv" name="成交金额" type="monotone" stroke="#0f66f5" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 4 }} isAnimationActive={false} />
                  <Line yAxisId="visitors" dataKey="visitors" name="商品访客" type="monotone" stroke="#06a9cf" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 4 }} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="realtime-chart-empty">
              <Clock3 aria-hidden="true" />
              <strong>已记录首份快照</strong>
              <span>下一次采集后将形成今日累计趋势和时段变化。</span>
            </div>
          )}
        </div>

        <aside className="realtime-panel realtime-movers-panel">
          <div className="realtime-panel-heading">
            <div>
              <h2>本时段变化</h2>
              <p>{previousTime ? `较 ${previousTime}` : '等待下一份快照'}</p>
            </div>
          </div>
          {previousSnapshot ? (
            <>
              <div className="realtime-mover-group">
                <h3>成交金额增长 TOP5</h3>
                {movers.gains.map(({ product, delta }, index) => (
                  <Link key={product.id} to={`/product/spu/${product.id}`}>
                    <span>{index + 1}</span>
                    <b>{product.name}</b>
                    <em className="is-positive">+{money(delta)}</em>
                  </Link>
                ))}
              </div>
              <div className="realtime-mover-group">
                <h3>成交金额下降 TOP5</h3>
                {movers.declines.map(({ product, delta }, index) => (
                  <Link key={product.id} to={`/product/spu/${product.id}`}>
                    <span>{index + 1}</span>
                    <b>{product.name}</b>
                    <em className="is-negative">{money(delta)}</em>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="realtime-mover-empty">
              <ArrowRight aria-hidden="true" />
              <p>首份快照已建立。下一次采集后，这里会列出增长和下滑最大的SPU。</p>
            </div>
          )}
        </aside>
      </section>

      <section className="realtime-panel realtime-ranking-panel">
        <div className="realtime-ranking-toolbar">
          <div>
            <h2>快照商品排行</h2>
            <div className="metric-switch" role="group" aria-label="实时商品层级">
              {(['spu', 'sku'] as ProductLevel[]).map((item) => (
                <button
                  type="button"
                  key={item}
                  className={level === item ? 'is-active' : ''}
                  aria-pressed={level === item}
                  onClick={() => setLevel(item)}
                >
                  {item.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-bar realtime-ranking-filters">
            <label className="search-control">
              <span className="sr-only">搜索实时商品</span>
              <Search aria-hidden="true" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={`搜索${level.toUpperCase()}名称或编码`}
              />
            </label>
            <label>
              <span className="sr-only">实时商品排序</span>
              <select value={sortKey} onChange={(event) => setSortKey(event.target.value as SortKey)}>
                <option value="gmv">按实时成交金额</option>
                <option value="delta" disabled={!previousSnapshot}>按较上次变化</option>
                <option value="soldItemCount">按成交件数</option>
                <option value="productVisitors">按商品访客</option>
                <option value="conversionRate">按转化率</option>
              </select>
            </label>
          </div>
        </div>

        {level === 'sku' && data.meta.skuExportLimitReached && (
          <div className="realtime-limit-warning">
            <TriangleAlert aria-hidden="true" />
            <span>SKU实时导出已达到京东1000行上限；成交金额和件数与SPU总额一致，但不把它表述为全量SKU覆盖。</span>
          </div>
        )}

        <div className="table-responsive realtime-table-wrap">
          <table className="realtime-table">
            <thead>
              <tr>
                <th>商品信息</th>
                <th>实时成交金额</th>
                <th>较上次</th>
                <th>成交件数</th>
                <th>商品访客</th>
                <th>浏览量</th>
                <th>转化率</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => {
                const metrics = effectiveMetrics(product, usePreviousProductMetrics)
                const delta = isLatest && product.previousMetrics
                  ? product.metrics.gmv - product.previousMetrics.gmv
                  : null
                return (
                  <tr key={`${product.level}:${product.id}`}>
                    <td>
                      <Link className="realtime-product-cell" to={`/product/${product.level}/${product.id}`}>
                        {product.image
                          ? <img src={product.image} alt="" />
                          : <span className="realtime-image-fallback">无图</span>}
                        <span>
                          <strong>{product.name}</strong>
                          <small>
                            {product.level.toUpperCase()} {product.id}
                            {product.merchantSku ? ` · 商家SKU ${product.merchantSku}` : ''}
                          </small>
                        </span>
                      </Link>
                    </td>
                    <td><strong>{money(metrics.gmv)}</strong></td>
                    <td>
                      {delta === null
                        ? <span className="realtime-no-delta">—</span>
                        : (
                          <span className={delta > 0 ? 'realtime-table-delta is-positive' : delta < 0 ? 'realtime-table-delta is-negative' : 'realtime-table-delta'}>
                            {delta > 0 ? <ArrowUpRight aria-hidden="true" /> : delta < 0 ? <ArrowDownRight aria-hidden="true" /> : null}
                            {delta > 0 ? '+' : ''}{money(delta)}
                          </span>
                        )}
                    </td>
                    <td>{integer(metrics.soldItemCount)}</td>
                    <td>{integer(metrics.productVisitors)}</td>
                    <td>{integer(metrics.productPageviews)}</td>
                    <td>{percent(metrics.conversionRate)}</td>
                  </tr>
                )
              })}
              {!products.length && (
                <tr><td colSpan={7} className="realtime-empty-row">没有匹配的实时商品。</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="realtime-table-footnote">当前显示前 {products.length} 条；商品行可进入现有SPU/SKU历史详情。</p>
      </section>

      <footer className="realtime-source-footer">
        <span>数据来源：京东商智 · 商品明细 · 历史实时快照</span>
        <span>页面更新时间：{dateTimeLabel(latestSnapshot.pageUpdatedAt)}</span>
        <span>采集时间：{dateTimeLabel(latestSnapshot.collectedAt)}</span>
        <span>快照次数：{data.meta.snapshotCount}</span>
        <strong>暂不含广告费用与净利润</strong>
      </footer>
    </div>
  )
}
